// A return step waiting on the OMS API (2026-10-29,
// docs/decisions/2026-10-29-return-label-pending-rma.md; docs/
// pipeline-behaviors.md "Return label pending the RMA number").
//
// Two kinds of wait, one table (pending_return_labels) and one sweep
// (server/src/services/return-label-sweep.js):
//   rma_number       the RMA was created but the OMS API has not assigned its
//                    number (RMA0000...) yet; only the return label waits.
//   zero_dollar_rma  the reason code makes the OMS API create a $0 RMA after
//                    the refund or replacement; CXHub watches for it and,
//                    only when a person turned on the optional label step,
//                    makes and sends the label once it exists (2026-10-30:
//                    off by default).
//
// Everything a CSR or an admin reads says "OMS API", never "TRQ" (Jenn,
// 2026-10-29). Pure: no calls. Used by the server (action-executor.js, the
// sweep, the routes) and the panel (PendingLabelChecklist.jsx,
// ZeroDollarRmaBanner.jsx, SuggestionPanel.jsx).

export const PENDING_KIND_RMA_NUMBER = 'rma_number'
export const PENDING_KIND_ZERO_DOLLAR = 'zero_dollar_rma'

// The step's state while it waits (the step result's stepState).
export const LABEL_PENDING_STEP_STATE = 'label_pending_rma_number'
export const ZERO_DOLLAR_PENDING_STEP_STATE = 'zero_dollar_rma_pending'

// The step's title chip while it waits.
export const PENDING_CHIP_TEXT = Object.freeze({
  [PENDING_KIND_RMA_NUMBER]: 'Waiting on OMS API: return label',
  [PENDING_KIND_ZERO_DOLLAR]: 'Waiting on OMS API: $0 RMA',
})

// Shown beside the spinner while Process waits for the number (up to 5 s).
export const RMA_NUMBER_WAIT_TEXT = 'Waiting for the OMS API to assign the RMA number…'

// How long the sweep keeps waiting before it gives up and reopens the
// contact: 24 hours for the RMA number, 48 for the $0 RMA.
export const PENDING_DEADLINE_HOURS = Object.freeze({
  [PENDING_KIND_RMA_NUMBER]: 24,
  [PENDING_KIND_ZERO_DOLLAR]: 48,
})

// How often the sweep checks: every 2 minutes for the number, every 5 for
// the $0 RMA.
export const PENDING_CHECK_EVERY_MINUTES = Object.freeze({
  [PENDING_KIND_RMA_NUMBER]: 2,
  [PENDING_KIND_ZERO_DOLLAR]: 5,
})

// The banner's typical time shows once there are this many measured $0 RMAs.
export const ZERO_DOLLAR_TIMING_MIN_SAMPLES = 20

// The order note at Process when the label waits on the number.
export const LABEL_PENDING_NOTE = "Return label pending: the OMS API hasn't assigned the RMA number yet; CXHub will create the label and send it automatically."

const isHtml = (text) => /<[a-z][^>]*>/i.test(String(text ?? ''))

// "email" or "eBay message": how the label reaches this customer.
export function labelVia(channelKind) {
  return channelKind === 'ebay' ? 'eBay message' : 'email'
}

// The reply's sentence in place of the label link(s) (Part 2.2), and the
// reply's label sentence for a $0 RMA with the label checked (Part 3.5): the
// same words for both.
export function labelToFollowSentence(channelKind) {
  return `Your return label will follow in a separate ${labelVia(channelKind)} once your return is set up.`
}

const SENTENCE_RE = /Your return label will follow in a separate (?:email|eBay message) once your return is set up\./

export function hasLabelToFollow(text) {
  return SENTENCE_RE.test(String(text ?? ''))
}

// The reply with the label link placeholder (and its line) replaced by the
// "label will follow" sentence; a reply with no placeholder gets the
// sentence as its own paragraph above the signature (Jenn, 2026-10-29)
// when `signature` (the exact trailing signature or sign-off the reply was
// built with) ends it, else at the end. Idempotent.
export function withLabelToFollow(text, channelKind, { token = 'INSERTLABELLINK', signature = null } = {}) {
  const raw = String(text ?? '')
  if (hasLabelToFollow(raw)) return raw
  const sentence = labelToFollowSentence(channelKind)
  if (raw.includes(token)) {
    if (isHtml(raw)) return raw.split(token).join(sentence)
    const lines = raw.split('\n')
    const at = lines.findIndex((l) => l.includes(token))
    lines[at] = sentence
    // Any further copies of the token go too.
    return lines.join('\n').split(token).join('')
  }
  if (!raw.trim()) return sentence
  const sig = typeof signature === 'string' ? signature.trim() : ''
  const trimmed = raw.replace(/\s+$/, '')
  if (sig && trimmed.endsWith(sig) && trimmed.length > sig.length) {
    const body = trimmed.slice(0, trimmed.length - sig.length)
    if (isHtml(raw)) return `${body}<p>${sentence}</p>${trimmed.slice(body.length)}`
    return `${body.replace(/\s+$/, '')}\n\n${sentence}\n\n${sig}`
  }
  if (isHtml(raw)) return `${raw}<p>${sentence}</p>`
  return `${raw.replace(/\s+$/, '')}\n\n${sentence}`
}

// The reply without the sentence (the $0 RMA label unchecked).
export function withoutLabelToFollow(text) {
  const raw = String(text ?? '')
  if (!hasLabelToFollow(raw)) return raw
  return raw
    .replace(new RegExp(`<p>\\s*${SENTENCE_RE.source}\\s*</p>`), '')
    .replace(new RegExp(`\\n*${SENTENCE_RE.source}`), '')
}

// --- The optional $0 RMA label step (2026-10-30) ------------------------------
//
// docs/decisions/2026-10-30-zero-dollar-label-off-by-default.md. Off by
// default for every $0 RMA reason code: a label is made (and promised in the
// reply) only when a person turned the optional step on. One rule for every
// reader: the panels, the shadow panel, the executor and the sweep.

// The step input's choice: on only when a person turned it on.
export function zeroDollarLabelOn(input) {
  return input?.zeroDollarLabel === true
}

// A pending_return_labels row of kind zero_dollar_rma makes its label only
// when it was saved with send_label true AND a person's choice is recorded
// on it (labelRequestedBy). A row saved before 2026-10-30 has no recorded
// choice, so it makes none.
// A person turning it off again (labelUncheckedAt) ends the watch
// (2026-11-02: with no label requested there is no watch).
export function zeroDollarRowSendsLabel(row) {
  return row?.sendLabel === true && Boolean(row?.labelRequestedBy ?? row?.labelData?.labelRequestedBy) && !row?.labelUncheckedAt
}

// --- The $0 RMA watch (2026-11-02) ----------------------------------------------
//
// docs/decisions/2026-11-02-zero-dollar-watch-only-for-a-label.md. The watch
// runs only for a requested label; its reads keep retrying for 2 hours
// before the row fails; a failure while looking for the $0 RMA goes on the
// $0 RMA line, in plain words.

// The note at Process for a $0 RMA reason with no label requested.
export function zeroDollarNotWatchingNote(reasonCode) {
  return `$0 RMA: the OMS API will create a $0 RMA for reason ${reasonCode}. No return label was requested, so CXHub isn't watching for it.`
}

// The $0 RMA line of a watch ended because no label was requested.
export const ZERO_DOLLAR_WATCH_STOPPED_TEXT = '$0 RMA watch stopped: no return label requested'

// Failed reads of the order keep the watch waiting until they have failed
// for this long in a row (no successful read in that time).
export const ZERO_DOLLAR_READ_RETRY_HOURS = 2

// The checklist line and the Failures entry when the order could not be
// read for ZERO_DOLLAR_READ_RETRY_HOURS (or a read was refused).
export function zeroDollarReadFailureText({ reasonCode = null, hours = null, lastTriedAt = null, error = null } = {}) {
  const what = `the $0 RMA (reason ${reasonCode ?? '?'})`
  const last = formatEtTime(lastTriedAt)
  const head = hours != null
    ? `CXHub couldn't reach the OMS API to check for ${what} for ${hours} hour${hours === 1 ? '' : 's'}${last ? `, last tried ${last}` : ''}`
    : `CXHub couldn't check the order for ${what}${last ? ` (${last})` : ''}`
  return `${head}${error ? `: ${error}` : ''}. Check the order for the $0 RMA and make the return label by hand.`
}

// The watch's line in the Activity Log (one line per watch, updated on each
// check): "$0 RMA watch (reason 103): checked the order 104 times,
// 10/30 4:21 PM – 10/31 12:53 AM ET; last: no new $0 RMA". With no times
// (older entries) the range is left out.
export function zeroDollarWatchLine({ reasonCode = null, count = 0, firstAt = null, lastAt = null, last = null } = {}) {
  const first = formatEtTime(firstAt)
  const end = formatEtTime(lastAt)
  const range = first && end ? (first === end ? first : `${first.replace(/ ET$/, '')} – ${end}`) : (end ?? first ?? null)
  const head = `$0 RMA watch${reasonCode != null ? ` (reason ${reasonCode})` : ''}: checked the order ${count} time${count === 1 ? '' : 's'}`
  return `${head}${range ? `, ${range}` : ''}${last ? `; last: ${last}` : ''}`
}

export const ZERO_DOLLAR_LABEL_NOT_REQUESTED = 'Return label not requested'

// The $0 RMA label choice after Process (2026-11-03): read only, the choice
// that was made. The replacement's (or refund's) $0 RMA box is the only
// return label control; a label needed later is made from Other Actions or
// the Console.
export function zeroDollarLabelChoiceText(checked) {
  return checked ? 'Return label: will be sent when the $0 RMA is created' : 'Return label: not requested'
}

// The checklist of a $0 RMA row that makes no label: the label lines not
// done yet replaced by one skipped "Return label not requested" line.
// Idempotent.
export function withoutRequestedLabelLines(lines) {
  const list = (Array.isArray(lines) ? lines : []).filter((l) => !((l?.key === 'return_label' || l?.key === 'label_sent') && l?.state !== 'done'))
  if (list.some((l) => l?.key === 'no_label' || l?.key === 'return_label')) return list
  return [...list, checkLine('no_label', 'skipped', ZERO_DOLLAR_LABEL_NOT_REQUESTED)]
}

// The $0 RMA label checkbox's effect on the drafted reply: the sentence in
// when checked, out when unchecked; the person's other edits stay.
export function applyZeroDollarLabelSentence(text, { checked, channelKind }) {
  return checked ? withLabelToFollow(withoutLabelToFollow(text), channelKind, { token: '\u0000none' }) : withoutLabelToFollow(text)
}

// --- Times ---------------------------------------------------------------------

export function formatEtTime(at) {
  if (!at) return null
  const d = at instanceof Date ? at : new Date(at)
  if (Number.isNaN(d.getTime())) return null
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York', month: '2-digit', day: '2-digit', hour: 'numeric', minute: '2-digit', hour12: true,
  }).formatToParts(d).map((p) => [p.type, p.value]))
  return `${parts.month}/${parts.day} ${parts.hour}:${parts.minute} ${String(parts.dayPeriod ?? '').toUpperCase()} ET`
}

// An API call's time in the Activity Log (2026-11-02): "10/30/2026 4:21:07 PM
// ET". null when there is none (an older entry): never a made-up time.
export function formatEtDateTimeSeconds(at) {
  if (!at) return null
  const d = at instanceof Date ? at : new Date(at)
  if (Number.isNaN(d.getTime())) return null
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit', hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true,
  }).formatToParts(d).map((p) => [p.type, p.value]))
  return `${parts.month}/${parts.day}/${parts.year} ${parts.hour}:${parts.minute}:${parts.second} ${String(parts.dayPeriod ?? '').toUpperCase()} ET`
}

// "about 3 hours" / "about 40 minutes" for a typical wait in ms.
export function typicalWaitWords(ms) {
  const n = Number(ms)
  if (!Number.isFinite(n) || n <= 0) return null
  const minutes = Math.round(n / 60000)
  if (minutes < 90) return `about ${Math.max(1, minutes)} minute${minutes === 1 ? '' : 's'}`
  const hours = Math.round(n / 3600000)
  return `about ${hours} hour${hours === 1 ? '' : 's'}`
}

// --- Banners -------------------------------------------------------------------

// The $0 RMA banner before Process (Part 3.1): two lines.
//   code: the reason code; what: 'refund' | 'replacement';
//   typical: the typical wait in words, or null ("a few hours");
//   checked: the optional label step, turned on (off by default, 2026-10-30).
export function zeroDollarBannerLines({ code, what = 'refund', typical = null, checked = false }) {
  return [
    `$0 RMA: the OMS API will create a $0 RMA for reason ${code} after this ${what === 'replacement' ? 'replacement' : 'refund'}, usually within ${typical || 'a few hours'}.`,
    checked ? 'CXHub will create the return label when it appears and send it to the customer.' : 'No return label will be sent. Turn on the optional label step to send one.',
  ]
}

// The optional step's line under the reason code, unchecked by default.
export const ZERO_DOLLAR_LABEL_CHECKBOX_TEXT = 'Optional: create and send a return label when the $0 RMA is created'

// The banner on a contact whose order has a step waiting on the OMS API.
export function orderWaitingBannerText(row) {
  const since = formatEtTime(row?.createdAt) ?? 'earlier'
  if (row?.kind === PENDING_KIND_ZERO_DOLLAR) {
    return !zeroDollarRowSendsLabel(row)
      ? `This order is waiting on the OMS API's $0 RMA (since ${since}); no return label will be sent for it.`
      : `Return label for this order is waiting on the OMS API's $0 RMA (since ${since}); CXHub will send it automatically.`
  }
  return `Return label for this order is waiting on the OMS API (since ${since}); CXHub will send it automatically.`
}

// The first What's next line of a contact reopened because the wait ran out.
export function timedOutNextStep(row) {
  const since = formatEtTime(row?.createdAt) ?? 'earlier'
  if (row?.kind === PENDING_KIND_ZERO_DOLLAR) return `The OMS API hasn't created the $0 RMA for reason ${row?.reasonCode ?? '?'} yet (waiting since ${since}); check the order in the Console.`
  return `Return label still waiting on the OMS API's RMA number since ${since}; check the RMA in the Console.`
}

// The first What's next line, and the Failures entry's next step, of a row
// that failed (2026-11-04): the stage it failed at, never the waiting text.
//   phase 'zero_dollar_rma': the $0 RMA could not be checked;
//   phase 'label_sent': the label was made but not sent;
//   else: the label was not finished.
export function failedNextStep(row, { phase = null, error = null } = {}) {
  const rma = row?.erpRmaId ?? row?.trqRmaId ?? null
  const why = String(error ?? '').trim()
  if (phase === 'zero_dollar_rma' || (row?.kind === PENDING_KIND_ZERO_DOLLAR && !rma)) {
    return `CXHub couldn't check the order for the $0 RMA (reason ${row?.reasonCode ?? '?'}); check the order in the Console and make the return label by hand.`
  }
  if (phase === 'label_sent') return `${labelNotSentText(rma)}${why ? `: ${why}` : ''}`
  return `Return label for RMA ${rma ?? '?'} was not finished${why ? `: ${why}` : ''}; check the RMA in the Console.`
}

// "Return label for RMA <n> was made but not sent" (2026-11-04).
export function labelNotSentText(erpRmaId) {
  return `Return label for RMA ${erpRmaId ?? '?'} was made but not sent`
}

// The Failures entry's message of a row that failed (2026-11-04, one text
// for the Failures row and the contact's What's next line): the $0 RMA not
// found, the label made but not sent, or the label not finished.
export function failedRowMessage(row, { error = null, phase = null } = {}) {
  if (row?.kind !== PENDING_KIND_RMA_NUMBER && (phase === 'zero_dollar_rma' || !(row?.erpRmaId ?? row?.trqRmaId))) {
    return error ?? `CXHub couldn't check the order for the $0 RMA (reason ${row?.reasonCode ?? '?'}). Check the order for the $0 RMA and make the return label by hand.`
  }
  if (phase === 'label_sent') return `${labelNotSentText(row?.erpRmaId ?? row?.trqRmaId)}: ${error ?? 'unknown error'}`
  return `Return label for RMA ${row?.erpRmaId ?? row?.trqRmaId ?? '?'} was not finished: ${error ?? 'unknown error'}`
}

// The stage a failed row stopped at, from its checklist (2026-11-04): the
// send line failed ('label_sent': the label was made), the $0 RMA line
// failed ('zero_dollar_rma'), else null.
export function failedPhaseOfRow(row) {
  const lines = Array.isArray(row?.checklist) ? row.checklist : []
  const failed = (key) => lines.some((l) => l?.key === key && l?.state === 'failed')
  if (failed('label_sent')) return 'label_sent'
  if (failed('zero_dollar_rma')) return 'zero_dollar_rma'
  return null
}

// The contact's What's next line of a failed row (2026-11-04, Jenn): the
// same text as its Failures entry, from the row's saved error and stage.
export function failedRowNextStep(row) {
  return failedRowMessage(row, { error: row?.lastError ?? null, phase: failedPhaseOfRow(row) })
}

// --- The checklist ------------------------------------------------------------
//
// Stored on the row as [{ key, state, label, id?, at?, detail?, error? }].
// state: done | waiting | ready | failed | skipped.

export const CHECK_STATE_ICON = Object.freeze({ done: '✓', waiting: '⏳', ready: '○', failed: '✕', skipped: '—' })

export function checkLine(key, state, label, extra = {}) {
  const line = { key, state, label }
  for (const k of ['id', 'at', 'detail', 'error', 'checked', 'after']) if (extra[k] != null && extra[k] !== '') line[k] = extra[k]
  return line
}

// The checklist a row starts with.
//   kind, trqRmaId, ebayPath (eBay approval line), approvedAt, noteAt,
//   noteError, reasonCode, createdWhat ('refund'|'replacement'), createdId,
//   sendLabel (the optional label step turned on; off by default), at
//   (Process time)
export function initialChecklist({ kind, trqRmaId = null, ebayPath = false, ebayApproved = null, noteAt = null, noteError = null, reasonCode = null, createdWhat = null, createdId = null, sendLabel = false, at = null }) {
  const lines = []
  const note = noteError
    ? checkLine('order_note', 'failed', kind === PENDING_KIND_ZERO_DOLLAR ? `${createdWhat === 'replacement' ? 'Replacement' : 'Refund'} note added to the order` : 'Order note added', { error: noteError })
    : checkLine('order_note', 'done', kind === PENDING_KIND_ZERO_DOLLAR ? `${createdWhat === 'replacement' ? 'Replacement' : 'Refund'} note added to the order` : 'Order note added', { at: noteAt ?? at })
  if (kind === PENDING_KIND_RMA_NUMBER) {
    lines.push(checkLine('rma_created', 'done', 'RMA created', { id: trqRmaId, at }))
    if (ebayPath) {
      lines.push(ebayApproved?.error
        ? checkLine('ebay_approved', 'failed', 'eBay return approved', { error: ebayApproved.error })
        : checkLine('ebay_approved', 'done', `eBay return approved (RMA ${trqRmaId})`, { at: ebayApproved?.at ?? at }))
    }
    lines.push(note)
    lines.push(checkLine('reply', 'ready', 'Reply ready to send'))
    lines.push(checkLine('rma_number', 'waiting', 'RMA number from the OMS API', { detail: 'waiting' }))
    lines.push(checkLine('return_label', 'waiting', 'Return label', { detail: 'waiting on the RMA number' }))
    lines.push(checkLine('label_sent', 'waiting', 'Label sent to customer', { detail: 'waiting on the label' }))
    return lines
  }
  lines.push(checkLine('created', 'done', createdWhat === 'replacement' ? 'Replacement created' : 'Refund created', { id: createdId, at }))
  lines.push(note)
  lines.push(checkLine('reply', 'ready', 'Reply ready to send'))
  lines.push(checkLine('zero_dollar_rma', 'waiting', `$0 RMA from the OMS API (reason ${reasonCode})`, { detail: 'waiting' }))
  if (sendLabel) {
    lines.push(checkLine('return_label', 'waiting', 'Return label', { detail: 'waiting on the $0 RMA' }))
    lines.push(checkLine('label_sent', 'waiting', 'Label sent to customer', { detail: 'waiting on the label' }))
  } else {
    lines.push(checkLine('no_label', 'skipped', ZERO_DOLLAR_LABEL_NOT_REQUESTED))
  }
  return lines
}

// The checklist with one line replaced (by key); a missing key is appended.
export function withCheckLine(lines, line) {
  const list = Array.isArray(lines) ? lines.slice() : []
  const i = list.findIndex((l) => l?.key === line.key)
  if (i >= 0) list[i] = line
  else list.push(line)
  return list
}

// The checklist as shown: the reply line done once the reply went out
// (replySentAt), and the waiting line's "waiting since, checked" detail.
export function shownChecklist(row, { replySentAt = null } = {}) {
  const lines = Array.isArray(row?.checklist) ? row.checklist : []
  return lines.map((l) => {
    if (l.key === 'reply' && l.state === 'ready' && replySentAt) return { ...l, state: 'done', label: 'Reply sent', at: replySentAt }
    // A label made but not recorded (2026-11-02): the line names it, also
    // on a row that failed before the line had this wording.
    if (l.key === 'return_label' && l.state === 'failed' && !l.unrecorded && rowCanRecordLabelAgain(row)) {
      return unrecordedLabelLine(unrecordedMadeLabels(row), l.error ?? row?.lastError ?? null)
    }
    if ((l.key === 'rma_number' || l.key === 'zero_dollar_rma') && l.state === 'waiting') {
      const since = formatEtTime(row?.createdAt)
      const checked = formatEtTime(row?.lastCheckedAt)
      // The $0 RMA watch's latest check says why it is still waiting
      // (line.checked, 2026-11-02).
      if (l.checked) return { ...l, detail: `waiting since ${since ?? 'Process'}. ${l.checked}` }
      return { ...l, detail: `waiting since ${since ?? 'Process'}${checked ? `, checked ${checked}` : ''}` }
    }
    return l
  })
}

// The row's title chip text, or null once it is no longer waiting.
export function pendingChipText(row) {
  if (!row || row.status !== 'waiting') return null
  return PENDING_CHIP_TEXT[row.kind] ?? null
}

// --- A label made but not recorded on its RMA (2026-11-02) -------------------
// docs/decisions/2026-11-02-rma-label-erp-customer-id.md. The OMS API's
// recordRmaShippingLabel refused the label (e.g. no erpCustomerId): the label
// exists, the RMA does not show it and, for an email contact, the Console
// never emailed it. The row ends failed and nothing retries it. Only a
// person's "Record the label again" finishes it, with the label already made
// (never a second label).

const NO_CUSTOMER_ID_PREFIX = "Can't record the label on the RMA: "

// The labels the row made that are not recorded on the RMA, by tracking
// number.
export function unrecordedMadeLabels(row) {
  const made = Array.isArray(row?.labelData?.madeLabels) ? row.labelData.madeLabels : []
  return made.filter((m) => m?.trackingNumber && m.recorded !== true).map((m) => String(m.trackingNumber))
}

// "Record the label again" shows only for a failed row (never a timed-out
// one) whose label was made and not recorded, on an RMA CXHub knows.
export function rowCanRecordLabelAgain(row) {
  if (!row || row.status !== 'failed') return false
  // A $0 RMA watch ended as not needed (2026-11-02) never offers it.
  if ((row.endedAs ?? row.labelData?.endedAs) === 'not_needed') return false
  if (!row.trqRmaId || !row.erpRmaId || !row.trqOrderId) return false
  return unrecordedMadeLabels(row).length > 0
}

// The failed checklist line: "Return label made (<tracking>) but not
// recorded on the RMA: <reason>".
export function unrecordedLabelLine(trackingNumbers, reason) {
  const tns = (trackingNumbers ?? []).filter(Boolean).join(', ')
  const why = String(reason ?? '').startsWith(NO_CUSTOMER_ID_PREFIX) ? String(reason).slice(NO_CUSTOMER_ID_PREFIX.length) : String(reason ?? '').trim()
  return { key: 'return_label', state: 'failed', label: `Return label made (${tns}) but not recorded on the RMA${why ? `: ${why}` : ''}`, id: tns, unrecorded: true }
}

// The send line of a label made but not sent (2026-11-04): the RMA, the
// tracking number(s), the link(s) and the reason. The label is kept.
export function labelNotSentLine({ erpRmaId = null, trackingNumbers = [], urls = [], reason = null } = {}) {
  const tns = (trackingNumbers ?? []).filter(Boolean).join(', ')
  const links = (urls ?? []).filter(Boolean).join(' ')
  return checkLine('label_sent', 'failed', labelNotSentText(erpRmaId), { id: tns, detail: links ? `label link: ${links}` : null, error: reason })
}

// The checklist after "Record the label again" worked: the record line with
// who did it, and the send line.
export function recordedAgainLines(lines, { trackingNumbers, erpRmaId, by, at, sentVia }) {
  const tns = (trackingNumbers ?? []).filter(Boolean).join(', ')
  let out = withCheckLine(lines, checkLine('return_label', 'done', `Return label ${tns} recorded on ${erpRmaId}`, { id: tns, at, after: `(recorded again by ${by ?? 'a person'})` }))
  out = withCheckLine(out, checkLine('label_sent', 'done', sentVia === 'console_label_email' ? 'Label sent to customer (the Console label email)' : sentVia === 'ebay_return' ? 'Label sent to customer (on the eBay return)' : 'Label sent to customer (eBay message)', { at }))
  return out
}

// What the panel needs for the button and its confirm, or null: the labels,
// the RMA and how it is sent (email: the Console's label email; eBay: a
// message to the buyer). No ship-to, no contact.
export function recordAgainInfo(row) {
  if (!rowCanRecordLabelAgain(row)) return null
  return {
    trackingNumbers: unrecordedMadeLabels(row),
    erpRmaId: row.erpRmaId,
    channelKind: row.labelData?.channelKind === 'ebay' ? 'ebay' : 'email',
  }
}
